import Modal from 'components/commons/modal/Modal';
import { AggregateSourceTypes, IS_MONO_SUSPENDED } from 'config/consts';
import { useModal } from 'hooks/useModal';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import * as api from 'config/api';
import * as useAPI from 'hooks/useAPI';
import Loading from 'components/commons/loading/Loading';
import { useRouting } from 'hooks/useRouting';

export const needsMigrate = (
  result: api.GetUserBrokerResultItem[]
): boolean => {
  return result.some(
    item => item.AggregateSource === AggregateSourceTypes.テコテック
  );
};

const NeedsMigrateAccountItem = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>): React.ReactElement => {
  return (
    <>
      <div className={`flex flex-row items-center ${className ?? ''}`}>
        <div className={`mr-auto`}>{children}</div>
        <div className={`icon-alert text-upcolor mr-2`} />
        <div className={`text-upcolor`}>再連携が必要です</div>
      </div>
    </>
  );
};

export const useMigrateToMonoStorage = () => {
  const key = 'isShowedMigrateModal';
  const logout = () => {
    localStorage.removeItem(key);
  };
  const showedMigrateToMonoModal = () => {
    localStorage.setItem(key, 'true');
  };
  const isShowedMigrateToMonoModal = (): boolean => {
    return localStorage.getItem(key) === 'true';
  };
  return {
    isShowedMigrateToMonoModal,
    showedMigrateToMonoModal,
    cleanupShowedMigrateToMonoModalFlag: logout,
  };
};

const MigrateToMonoModal = ({
  hide,
  isShowModal,
  userBroker,
  deadline,
}: {
  isShowModal: boolean;
  hide: () => void;
  userBroker: api.GetUserBrokerResultItem[];
  deadline: Date;
}): React.ReactElement => {
  const needsMigrateAccounts = userBroker.filter(
    item => item.AggregateSource === AggregateSourceTypes.テコテック
  );
  const pushRouting = (({ pushRouting }) => (path: string) => (): void => {
    pushRouting({ path }, true);
  })(useRouting());
  const toAnotherServiceAlignment = pushRouting('/anotherservicealignment');
  const toSecurityAccounts = pushRouting('/securityaccounts');
  return (
    <>
      <Modal isShow={isShowModal} close={hide}>
        <div className="p-4 overflow-y-auto max-h-modal">
          <div className={`flex flex-row items-center pb-4`}>
            <div className="font-bold text-lg text-bluegray-700 mr-auto">
              登録口座の再連携が必要です
            </div>
            <div
              className={`icon-close text-lg text-bluegray-500 cursor-pointer`}
              onClick={hide}
            />
          </div>
          <div className="pb-4">
            口座連携に関する同意内容が更新されたため連携を再設定する必要があります。
          </div>
          {needsMigrateAccounts.map((item, i, arr) => {
            return (
              <NeedsMigrateAccountItem
                key={i}
                className={`${i !== arr.length - 1 ? 'mb-2' : ''}`}>
                {item.BrokerName}
              </NeedsMigrateAccountItem>
            );
          })}
          <div className={`bg-bluegray-300 my-2`} style={{ height: '2px' }} />
          <div className={`text-upcolor justify-start mb-3 font-bold`}>
            期限：{deadline.getFullYear()}年{deadline.getMonth() + 1}月
            {deadline.getDate()}日
          </div>
          <div className={`flex flex-col mb-4`}>
            <div className={``}>期限内に上記口座の再連携をお願いします。</div>
            <div className={``}>
              期限を過ぎると口座データの取得が一時停止され、再連携を行った時点で取得が再開されます。
            </div>
          </div>
          <div className="p-4">
            <div
              className="rounded-lg py-2 px-4 mb-4 mt-2 bg-tradewell text-white font-bold flex items-center cursor-pointer justify-center"
              onClick={toSecurityAccounts}>
              連携口座一覧
            </div>
            <div
              className={`text-bluegray-500 py-2 px-4 mb-4 text-center cursor-pointer`}
              onClick={toAnotherServiceAlignment}>
              外部サービスとの連携について
            </div>
            <div
              className={`text-bluegray-500 py-2 px-2 mb-4 text-center cursor-pointer`}
              onClick={hide}>
              閉じる
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export const useBrokers = (): {
  userBroker: useAPI.State<'state', api.GetUserBrokerResultItem[]>;
  getUserBroker: () => Promise<void>;
} => {
  const [state, setState] = useState<
    useAPI.State<'state', api.GetUserBrokerResultItem[]>
  >(useAPI.loading);
  const getUserBrokers = async (): Promise<void> => {
    const result = await api.getUserBroker();
    setState(useAPI.loaded({ state: result.Items }));
  };
  return { userBroker: state, getUserBroker: getUserBrokers };
};

// eslint-disable-next-line no-empty-pattern
const WithMigrateToMonoModalInternal = ({}) => ({
  deadline,
}: {
  deadline: Date;
}) =>
  function WithMigrateToMonoModal({
    children,
    userBroker,
    modal,
  }: PropsWithChildren<{
    userBroker: useAPI.State<'state', api.GetUserBrokerResultItem[]>;
    modal?: ReturnType<typeof useModal>;
  }>): React.ReactElement {
    const {
      isShowedMigrateToMonoModal,
      showedMigrateToMonoModal,
    } = useMigrateToMonoStorage();
    const [isShowedMigrateModal] = useState(() => isShowedMigrateToMonoModal());
    const modal_ = useModal();
    const [isShowModal, { showModal: showModal_, hideModal }] = modal ?? modal_;
    const showModal = () => {
      showModal_();
      showedMigrateToMonoModal();
    };
    useEffect(() => {
      if (
        // MEMO: MONO障害対応中は再連携誘導モーダルを表示しない
        !IS_MONO_SUSPENDED &&
        useAPI.isLoaded(userBroker) &&
        needsMigrate(userBroker.state) &&
        !isShowedMigrateModal
      ) {
        showModal();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userBroker, isShowedMigrateModal]);
    if (useAPI.isLoading(userBroker))
      return <Loading loading style={{ marginTop: '3rem' }} />;

    return (
      <>
        <div className={''}>{children}</div>
        <MigrateToMonoModal
          {...{
            isShowModal: isShowModal && !IS_MONO_SUSPENDED,
            hide: hideModal,
            userBroker: userBroker.state,
            deadline,
          }}
        />
      </>
    );
  };

export const WithMigrateToMonoModal = WithMigrateToMonoModalInternal({})({
  deadline: new Date('2022-11-30T00:00:00'),
});

export const NeedsMigrateStickyNote = ({
  onClickDetail,
}: {
  onClickDetail: () => void;
}): React.ReactElement => {
  return (
    <>
      <div className={`flex flex-col rounded shadow`}>
        <div className={`bg-orange`} style={{ height: '2px' }} />
        <div
          className={`flex flex-row items-center px-5 py-3`}
          style={{ backgroundColor: `rgba(${0xff}, ${0xee}, ${0xf0}, 1)` }}>
          <div className={`text-upcolor icon-alert mr-2`}></div>
          <div className={`font-bold mr-auto`}>登録口座の再連携が必要です</div>
          <div
            className={`text-bluegray-400 cursor-pointer`}
            onClick={onClickDetail}>
            詳細
          </div>
        </div>
      </div>
    </>
  );
};

export const NeedsMigrateCard = ({
  migrate,
  onClickAgreement,
}: {
  onClickAgreement: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  migrate: (e: any) => void;
}): React.ReactElement => {
  return (
    <>
      <div
        className={`flex flex-row p-5 rounded-lg`}
        style={{ backgroundColor: `rgba(${0xff}, ${0xee}, ${0xf0}, 1)` }}>
        <div className={`icon-alert items-start mr-2 mt-1 text-upcolor`}></div>
        <div className={`flex flex-col gap-2`}>
          <div className={`justify-start`}>
            口座連携に関する同意内容が更新されたため連携を再設定する必要があります。
          </div>
          <div
            className={`flex flex-row items-center text-gray-700 justify-start`}
            onClick={(e): void => {
              e.stopPropagation();
              onClickAgreement();
            }}>
            <div className={`mr-1`}>外部サービスとの連携について</div>
          </div>
          <div className={`text-link ml-auto`} onClick={migrate}>
            再連携する
          </div>
        </div>
      </div>
    </>
  );
};
